import { useRef, useState } from 'react';

import {
  type CardContent,
  type LearningCard,
  learningDraftSchema,
  newLearningCard,
} from '../../shared/learning';
import type { LocalRecord, Note } from '../../shared/model';

import { request, workspace } from '../lib/engine';
import { ActionIconButton, Button, Disclosure, Input, Modal, Select, Textarea } from './ui';

export function LearningCapture({
  records,
  available,
  editing,
  onClose,
}: {
  records: LocalRecord[];
  available: boolean;
  editing?: { id: string; value: LearningCard };
  onClose: () => void;
}) {
  const [source, setSource] = useState(editing?.value.source ?? '');
  const [noteId, setNoteId] = useState(editing?.value.noteId ?? '');
  const [reverse, setReverse] = useState(false);

  const [cards, setCards] = useState<(CardContent & { id: string })[]>(
    editing ? [{ ...editing.value, id: editing.id }] : [],
  );

  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const notes = records.filter(r => r.kind === 'note' && !r.deleted && !(r.value as Note).trashed);

  async function perform(action: () => Promise<void>) {
    if (locked.current) {
      return;
    }

    locked.current = true;
    setBusy(true);
    setError('');

    try {
      await action();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      locked.current = false;
      setBusy(false);
    }
  }

  function update(id: string, field: keyof CardContent, value: string) {
    setCards(previous =>
      previous.map(card => (card.id === id ? { ...card, [field]: value } : card)),
    );
  }

  async function save() {
    const checked = learningDraftSchema.parse({ cards }).cards;

    if (editing) {
      const current = workspace.snapshot().records.find(r => r.id === editing.id)?.value as
        | LearningCard
        | undefined;

      if (!current) {
        throw new Error('This card is unavailable.');
      }

      await workspace.save(editing.id, 'learning-card', {
        ...current,
        ...checked[0],
        noteId: noteId || null,
      });
    } else {
      await workspace.saveMany(
        checked.map((card, index) => ({
          id: cards[index].id,
          kind: 'learning-card',
          value: newLearningCard(card, source, noteId || null),
        })),
      );
    }

    onClose();
  }

  return (
    <Modal
      open
      title={editing ? 'Edit learning card' : 'Learn something'}
      busy={busy}
      onClose={onClose}
      onSubmit={e => {
        e.preventDefault();
        void perform(save);
      }}
      className="learning-editor"
    >
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {!editing && cards.length === 0 ? (
        <>
          <label>
            What did you learn?
            <Textarea
              value={source}
              onChange={e => setSource(e.target.value)}
              maxLength={12000}
              disabled={busy}
              placeholder="A fact, a word you keep forgetting, or a concept you want to explain…"
            />
          </label>
          <label className="inline-check">
            <Input
              type="checkbox"
              checked={reverse}
              onChange={e => setReverse(e.target.checked)}
              disabled={busy}
            />
            Draft reverse-direction cards when useful
          </label>
          <p className="muted">
            Draft with AI sends this capture to DeepSeek. You will check and approve every card
            before it enters review.
          </p>
          {!available && (
            <p className="notice">
              AI drafting is unavailable. You can write and approve your own cards.
            </p>
          )}
          <div className="button-row wrap">
            <Button
              type="button"
              variant="primary"
              disabled={busy || !available || !source.trim()}
              onClick={() =>
                void perform(async () => {
                  const result = learningDraftSchema.parse(
                    await request('/api/learning/draft', { source, reverse }),
                  );

                  setCards(result.cards.map(card => ({ ...card, id: crypto.randomUUID() })));
                })
              }
            >
              {busy ? 'Preparing cards…' : 'Draft with AI'}
            </Button>
            <Button
              type="button"
              disabled={busy}
              onClick={() =>
                setCards([{ id: crypto.randomUUID(), question: '', answer: '', additions: '' }])
              }
            >
              Write manually
            </Button>
            <Button type="button" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </>
      ) : (
        <>
          {source && (
            <Disclosure summary="Original capture" open>
              <p className="learning-text">{source}</p>
            </Disclosure>
          )}
          <label>
            Linked note (optional)
            <Select value={noteId} onChange={e => setNoteId(e.target.value)} disabled={busy}>
              <option value="">None</option>
              {noteId && !notes.some(r => r.id === noteId) && (
                <option value={noteId}>Unavailable note</option>
              )}
              {notes.map(r => (
                <option key={r.id} value={r.id}>
                  {(r.value as Note).title}
                </option>
              ))}
            </Select>
          </label>
          {cards.map((card, index) => (
            <fieldset key={card.id} disabled={busy} className="learning-draft">
              <legend>Card {index + 1}</legend>
              <label>
                Question
                <Textarea
                  required
                  value={card.question}
                  maxLength={2000}
                  onChange={e => update(card.id, 'question', e.target.value)}
                />
              </label>
              <label>
                Reference answer
                <Textarea
                  required
                  value={card.answer}
                  maxLength={8000}
                  onChange={e => update(card.id, 'answer', e.target.value)}
                />
              </label>
              <label>
                Additions, corrections, or uncertainty
                <Textarea
                  value={card.additions}
                  maxLength={4000}
                  onChange={e => update(card.id, 'additions', e.target.value)}
                />
              </label>
              {!editing && cards.length > 1 && (
                <ActionIconButton
                  action="trash"
                  label={`Remove draft card ${index + 1}`}
                  type="button"
                  onClick={() => setCards(previous => previous.filter(c => c.id !== card.id))}
                />
              )}
            </fieldset>
          ))}
          {!editing && (
            <div className="button-row wrap">
              <ActionIconButton
                action="add"
                label="Add draft card"
                type="button"
                disabled={busy || cards.length >= 8}
                onClick={() =>
                  setCards(previous => [
                    ...previous,
                    { id: crypto.randomUUID(), question: '', answer: '', additions: '' },
                  ])
                }
              />
              <Button type="button" disabled={busy} onClick={() => setCards([])}>
                Back to capture
              </Button>
            </div>
          )}
          <p className="muted">
            Check that each answer is correct and each question tests one thing. Approved cards wait
            their turn; up to five new cards enter review each day.
          </p>
          <div className="button-row wrap">
            <Button type="submit" variant="primary" disabled={busy}>
              {busy
                ? 'Saving…'
                : editing
                  ? 'Save card'
                  : `Approve ${cards.length === 1 ? 'card' : `all ${cards.length} cards`}`}
            </Button>
            <Button type="button" disabled={busy} onClick={onClose}>
              Cancel
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
}
